import { CALENDAR_EVENT_KIND_LABELS, type CalendarEvent } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  CALENDAR_TONES,
  eventsByDate,
  monthRange,
  monthWeeks,
  shiftMonth,
} from "../../../features/compliance/compliance-values.js";
import { useComplianceCalendar } from "../../../features/compliance/queries.js";
import { MaintenancePage, StationFilterField } from "../../../features/maintenance/shared.js";
import { parseAppLink } from "../../../features/alarms/alarm-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined),
  station: z.string().uuid().optional().catch(undefined),
  view: z.enum(["month", "list"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/uyum/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CalendarRoute,
});

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

/**
 * Uyum takvimi (Prompt 23 web, `GET /compliance/calendar`): belge bitişleri ve hatırlatma günleri, denetim ziyaretleri,
 * düzeltici faaliyet son tarihleri, bakım iş emri çözüm son anları ve plan vadeleri — zincir (tüm istasyonlar) ya da istasyon
 * görünümü; ay ızgarası ve liste. Her olay kendi ekranına bağlanır.
 */
function CalendarRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MaintenancePage
      title={t("menu.complianceCalendar")}
      description={t("compliance.calendar.description")}
      requirement="calendar"
    >
      <CalendarBody />
    </MaintenancePage>
  );
}

function CalendarBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const today = toIstanbulDate(new Date());
  const month = search.month ?? today.slice(0, 7);
  const view = search.view ?? "month";
  const { from, to } = monthRange(month);
  const calendar = useComplianceCalendar({ from, to, stationId: search.station ?? null });
  const setSearch = (next: { month?: string; station?: string | null; view?: "month" | "list" }) => {
    const station = next.station !== undefined ? next.station : (search.station ?? null);
    void navigate({
      to: ".",
      search: {
        month: next.month ?? month,
        ...(station ? { station } : {}),
        ...((next.view ?? view) !== "month" ? { view: next.view ?? view } : {}),
      } as never,
      replace: true,
    });
  };
  const [year, monthIndex] = month.split("-").map(Number) as [number, number];
  const events = calendar.data?.events ?? [];
  const grouped = eventsByDate(events);
  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label={t("compliance.calendar.previous")}
            onClick={() => setSearch({ month: shiftMonth(month, -1) })}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <h2 className="min-w-36 text-center text-base font-semibold" aria-live="polite">
            {MONTHS[monthIndex - 1]} {year}
          </h2>
          <Button
            variant="outline"
            size="sm"
            aria-label={t("compliance.calendar.next")}
            onClick={() => setSearch({ month: shiftMonth(month, 1) })}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSearch({ month: today.slice(0, 7) })}>
            {t("compliance.calendar.today")}
          </Button>
        </div>
        <StationFilterField value={search.station ?? null} onChange={(station) => setSearch({ station })} />
      </Card>
      {calendar.data && calendar.data.hiddenKinds.length > 0 ? (
        <Alert tone="info">
          {t("compliance.calendar.hidden", {
            kinds: calendar.data.hiddenKinds.map((kind) => CALENDAR_EVENT_KIND_LABELS[kind]).join(", "),
          })}
        </Alert>
      ) : null}
      {calendar.error ? (
        <Alert tone="danger">{errorMessage(calendar.error)}</Alert>
      ) : !calendar.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <Tabs value={view} onValueChange={(value) => setSearch({ view: value as "month" | "list" })}>
          <TabsList>
            <TabsTrigger value="month">{t("compliance.calendar.monthView")}</TabsTrigger>
            <TabsTrigger value="list">
              {t("compliance.calendar.listView", { count: events.length })}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="month">
            <div
              className="overflow-x-auto rounded-md border"
              tabIndex={0}
              role="group"
              aria-label={t("compliance.calendar.monthView")}
            >
              <table className="w-full min-w-[48rem] table-fixed text-sm">
                <caption className="sr-only">
                  {MONTHS[monthIndex - 1]} {year}
                </caption>
                <thead>
                  <tr>
                    {WEEKDAYS.map((day) => (
                      <th
                        key={day}
                        scope="col"
                        className="border-b p-2 text-left text-xs font-medium text-muted-foreground"
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthWeeks(month).map((week, index) => (
                    <tr key={index}>
                      {week.map((day, cell) => (
                        <td
                          key={day ?? `empty-${index}-${cell}`}
                          className={`h-28 border-b border-r p-1.5 align-top ${day === today ? "bg-info-subtle" : ""}`}
                        >
                          {day ? (
                            <div className="grid gap-1">
                              <span className="text-xs font-semibold tabular">
                                {Number(day.slice(8, 10))}
                              </span>
                              {(grouped.get(day) ?? []).slice(0, 4).map((event) => (
                                <EventChip key={event.key} event={event} />
                              ))}
                              {(grouped.get(day)?.length ?? 0) > 4 ? (
                                <span className="text-xs text-muted-foreground">
                                  {t("compliance.calendar.more", {
                                    count: (grouped.get(day)?.length ?? 0) - 4,
                                  })}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="list">
            {events.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">{t("compliance.calendar.empty")}</p>
            ) : (
              <ol className="grid gap-3">
                {[...grouped.entries()].map(([date, items]) => (
                  <li key={date} className="grid gap-2">
                    <h3 className="text-sm font-semibold">{formatDate(date)}</h3>
                    <ul className="grid gap-2">
                      {items.map((event) => (
                        <li
                          key={event.key}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                        >
                          <span className="grid gap-0.5">
                            <EventLink link={event.link} className="font-medium underline underline-offset-4">
                              {event.title}
                            </EventLink>
                            <span className="text-xs text-muted-foreground">
                              {event.stationCode ?? ""} · {CALENDAR_EVENT_KIND_LABELS[event.kind]}
                              {event.detail ? ` · ${event.detail}` : ""}
                            </span>
                          </span>
                          <StatusBadge tone={CALENDAR_TONES[event.tone]}>
                            {t(`compliance.calendar.tones.${event.tone}`)}
                          </StatusBadge>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </TabsContent>
        </Tabs>
      )}
    </>
  );
}

function EventChip({ event }: { event: CalendarEvent }): ReactElement {
  const tones = {
    INFO: "border-info/40 bg-info-subtle",
    WARNING: "border-warning/40 bg-warning-subtle",
    CRITICAL: "border-destructive/40 bg-danger-subtle",
    DONE: "border-success/40 bg-success-subtle",
  } as const;
  return (
    <EventLink
      link={event.link}
      className={`block truncate rounded border px-1.5 py-0.5 text-xs ${tones[event.tone]}`}
      title={`${event.stationCode ?? ""} · ${event.title}`}
    >
      {event.stationCode ? `${event.stationCode} · ` : ""}
      {event.title}
    </EventLink>
  );
}

/** Takvim olayının bağlantısı (sunucunun uygulama içi yolu; güvenli ayrıştırma — alarm bağlantılarıyla aynı kural). */
function EventLink({
  link,
  className,
  title,
  children,
}: {
  link: string;
  className: string;
  title?: string;
  children: ReactNode;
}): ReactElement {
  const target = parseAppLink(link);
  if (!target) return <span className={className}>{children}</span>;
  return (
    <Link
      to={target.to as never}
      search={target.search as never}
      className={className}
      {...(title ? { title } : {})}
    >
      {children}
    </Link>
  );
}
